import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * A última busca de uma listagem, para o "Voltar para a lista" devolver a pessoa ao mesmo recorte.
 *
 * Os filtros das listagens vivem na URL, e o "voltar" do detalhe apontava para a rota pura: quem
 * filtrava agosto, abria um chamado, julgava e voltava encontrava a lista sem filtro — e refazia o
 * filtro chamado a chamado (teste de uso 3, 25/09/2026). Guardado por aba (`sessionStorage`): duas
 * abas com recortes diferentes não se atropelam, e a lembrança some quando a aba fecha.
 *
 * Armazenamento é conveniência: sem ele (aba anônima, bloqueio do navegador), o "voltar" cai na
 * rota pura, que é o comportamento de antes, e nada quebra.
 */

const PREFIXO = 'gestao2:ultima-busca:'

/**
 * Lembra a busca da listagem a cada mudança de filtro.
 *
 * @param lista o caminho da listagem, como `/chamados`
 */
export function useLembrarBusca(lista: string): void {
  const { search } = useLocation()
  useEffect(() => {
    try {
      window.sessionStorage.setItem(PREFIXO + lista, search)
    } catch {
      // Sem armazenamento, o "voltar" leva à listagem sem filtro — que é o comportamento de antes.
    }
  }, [lista, search])
}

/**
 * O endereço da listagem com a última busca feita nesta aba.
 *
 * @param lista o caminho da listagem, como `/chamados`
 * @returns o caminho com a busca, ou só o caminho quando não há lembrança
 */
export function enderecoDaLista(lista: string): string {
  try {
    const busca = window.sessionStorage.getItem(PREFIXO + lista)
    return busca ? `${lista}${busca}` : lista
  } catch {
    return lista
  }
}
